// The music director (decision 0045: "adaptive, with layers fading in and out and moves between pieces on the bar";
// suggestion "music director: generative phrases plus loop transitions with mood holds"; audio cards 8, 9 and 13).
// Pure logic on a clock it is given: it schedules note events a little ahead (on a bar grid) and asks the engine to fade
// its layers; the engine plays the notes on instruments. So a test drives it with a fake clock and reads its log.
//
// - Moods as data (tempo, key, scale, chords, parts). A mood change takes effect on the next bar line; every mood
//   should share one key centre so a change never clashes.
// - Harmony (the music pass, 2026-10-19): a chord lasts `barsPerChord` bars; chords may carry extensions (sus2, add9,
//   maj7, 6/9); the pad and the arpeggio voice each chord nearest the voicing before it (theory.ts), so the harmony
//   moves by small steps instead of root-position jumps.
// - Form: with `form`, the piece is sections (e.g. A A' B A), each a named progression; the lead's material is a
//   two-bar motif, stated, repeated as a sequence on the next chord, varied (rhythm tweaked, long notes snapped to the
//   chord) and answered (a cadence: home on the root in A', on the third or fifth in A, the fifth in B). A contrasting
//   motif for B. Each mood keeps its motifs, so a theme comes back when its mood does. Without `form` the lead plays
//   free stepwise phrases with rests between (the first version).
// - Parts enter over time (`from`, bars after the mood starts); each section can rest parts; a velocity swell over each
//   section and small seeded humanising of timing and velocity.
// - The combat layer (a 16-step percussion and bass pattern on the same grid) enters on a bar line, its fade ending on
//   the downbeat so the bar lands at full volume ("enter on the last bar"); it holds `hold` seconds after the last
//   alert ends before leaving on a bar line, so it doesn't flip-flop at the edge of a fight.
// - The victory phrase lands on the next bar line: the combat layer drops, a short phrase plays on the sting layer
//   and the form starts again from its first chord (the tonic) under it.
// - Deterministic: every choice draws from the director's seeded generator; it never reads a clock of its own.
import { mulberry32, type Rng } from "./dsp";
import { blockVoicing, nearestIndex, pcOf, scalePool, snapToChord, voiceLead } from "./theory";

/** A section of a mood's form. */
export interface FormSection {
  /** The progression it plays: a key of the mood's `progressions`, or "A" for its `progression`. */
  chords: string;
  /** The lead's material: "A" the motif (stated, repeated, varied, answered on the third or fifth), "A2" the same
   *  answered home on the root, "B" a contrasting motif, "" no lead. */
  lead: "A" | "A2" | "B" | "";
  /** Another lead instrument for this section. */
  inst?: string;
  /** Parts resting in this section. */
  rest?: ("pad" | "bass" | "arp")[];
}

/** The melody. */
export interface LeadPart {
  inst: string;
  /** MIDI range of the line. */
  low: number;
  high: number;
  /** Free phrases (no `form`): bars per phrase, and rest bars between phrases. */
  phraseBars?: [number, number];
  restBars?: [number, number];
  /** Rhythm cells (beats; each sums to a bar). */
  cells: number[][];
  vel: number;
  /** Bars a motif lasts (default 2). */
  motifBars?: number;
  /** The bar (after the mood starts) the part enters (default 0). */
  from?: number;
}

/** Sustained chords. */
export interface PadPart {
  inst: string;
  /** Without `range`: a block chord at the root's octave + `octave` every bar (the first version). */
  octave: number;
  vel: number;
  /** Voice-led: the MIDI range and the voice count; one chord per chord change, held. */
  range?: [number, number];
  voices?: number;
  from?: number;
}

/** The bass line: the chord's root (plus `notes` semitones) on some beats of each bar. */
export interface BassPart {
  inst: string;
  octave: number;
  beats: number[];
  /** Semitones over the chord's root per beat (default 0). */
  notes?: number[];
  /** On a chord's last bar, the last beat walks to a scale step next to the coming chord's root. */
  walk?: boolean;
  vel: number;
  from?: number;
}

/** Broken chords: a pattern of indices into the voiced chord (low to high; past the top, an octave up; −1 rests). */
export interface ArpPart {
  inst: string;
  range: [number, number];
  voices: number;
  pattern: number[];
  /** Beats per step. */
  step: number;
  vel: number;
  /** How long a note rings, in steps (default 2). */
  ring?: number;
  from?: number;
}

/** A mood: a piece's tempo, key and parts. */
export interface MusicMood {
  id: string;
  bpm: number;
  beatsPerBar: number;
  /** The key centre (MIDI). */
  root: number;
  /** Scale degrees (semitones above the root, within an octave). */
  scale: number[];
  /** Chords, cycled: chord tones as semitones above the root (the root first, extensions welcome). */
  progression: number[][];
  /** Bars each chord lasts (default 1). */
  barsPerChord?: number;
  /** Named progressions for `form`. */
  progressions?: Record<string, number[][]>;
  /** The piece's sections, looped. */
  form?: FormSection[];
  lead?: LeadPart;
  pad?: PadPart;
  bass?: BassPart;
  arp?: ArpPart;
  /** A velocity swell over each section (0 flat; 0.2: ±20 % between its quiet ends and its loud middle). */
  swell?: number;
  /** Humanising: ± seconds of timing and ± a fraction of velocity per note, seeded. */
  humanise?: { time: number; vel: number };
}

/** A part of the combat layer: an instrument hit on some of a bar's 16 steps. */
export interface CombatPart {
  inst: string;
  steps: number[];
  /** Semitones above the bar's chord root (pitched parts), or an absolute MIDI note when `fixed`. */
  note: number;
  fixed?: boolean;
  vel: number;
}

/** Everything the director plays. */
export interface MusicTable {
  moods: MusicMood[];
  combat: {
    parts: CombatPart[];
    /** Seconds the layer stays after the last alert ends. */
    hold: number;
    /** Fade out (s). */
    fadeOut: number;
  };
  victory: {
    inst: string;
    /** Semitones above the root. */
    notes: number[];
    /** Beats between notes. */
    step: number;
    vel: number;
  };
}

/** Which part a note belongs to. */
export type MusicPart = "lead" | "pad" | "bass" | "arp" | "combat" | "victory";

/** Where a lead note sits in the form (for the motif checks). */
export interface PhraseMark {
  /** Sections since the mood started, the two-bar unit in it, and its role. */
  section: number;
  unit: number;
  role: "statement" | "repeat" | "variation" | "answer";
  material: "A" | "A2" | "B";
  /** Beats from the unit's start. */
  beat: number;
  /** Scale steps from the unit's anchor. */
  step: number;
}

/** A note to play. */
export interface NoteEvent {
  at: number;
  inst: string;
  midi: number;
  vel: number;
  /** Seconds it should sound (the engine's release). */
  dur: number;
  layer: MusicLayer;
  part?: MusicPart;
  /** The time on the grid before humanising. */
  grid?: number;
  phrase?: PhraseMark;
}

/** The music bus's layers. */
export type MusicLayer = "base" | "combat" | "sting";

/** What happened, for the handle and the tests. */
export interface MusicLogEntry {
  kind: "mood" | "combat-in" | "combat-out" | "victory";
  /** When it lands (a bar line). */
  at: number;
  /** When it was asked for. */
  asked: number;
  /** The director's clock when it placed this (the update that scheduled the bar): `at - by` is the lookahead's
   * share of the wait, `by - asked` the caller's (a stalled frame loop delays it; the director can't). */
  by: number;
  mood: string;
}

/** A chord as it sounded: from `at`, in `mood`. */
export interface ChordMark {
  at: number;
  mood: string;
  chord: number[];
  root: number;
}

/** The engine's side: play a note; fade a layer from its value at `start` to `target` over `seconds`; the tempo from
 *  `at` (a tempo-synced echo follows it); release every base-layer note still sounding at `at` (a mood change, or the
 *  form restarting under the victory phrase: a held chord mustn't ring on into the next piece). */
export interface MusicSink {
  note(e: NoteEvent): void;
  fade(layer: MusicLayer, target: number, start: number, seconds: number): void;
  tempo?(bpm: number, at: number): void;
  cut?(at: number): void;
}

/** Planted defects beside what they break: the music checks (voice leading, motif, chord tones, determinism, layer
 *  entries, dynamics) must go red with each on. */
export const MUSIC_FAULTS = {
  /** Every chord a root-position block at the bottom of the range: the voice-leading check goes red. */
  noVoiceLeading: false,
  /** The lead forgets its motif: every unit a fresh line: the motif check goes red. */
  noMotif: false,
  /** The pad and the arpeggio read the chord a change late: the chord-tone check goes red. */
  staleChord: false,
  /** The director draws from `Math.random`, not its seed: the determinism check goes red. */
  unseeded: false,
  /** Every part enters on the mood's first bar: the layer-entry check goes red. */
  allAtOnce: false,
  /** No swell and no humanising: the dynamics check goes red. */
  flat: false,
};

interface MotifNote {
  beat: number;
  dur: number;
  step: number;
}
interface Motif {
  notes: MotifNote[];
  beats: number;
}

const pick = <T>(rng: Rng, xs: readonly T[]): T => xs[Math.floor(rng() * xs.length)]!;

/** The director. */
export class MusicDirector {
  private readonly rng: Rng;
  private readonly moods = new Map<string, MusicMood>();
  private cur: MusicMood;
  private wanted: string;
  private wantedAt = 0;
  /** The bar grid: bar k of the current mood starts at t0 + k × barLen. */
  private t0 = 0;
  private barIndex = 0;
  private nextBar: number | null = null;
  private combatOn = false;
  private alertOn = false;
  private alertOnAt = 0;
  private alertOffAt = -Infinity;
  private victoryAsked: number | null = null;
  // free phrases (no form)
  private phraseLeft = 0;
  private restLeft = 1;
  private leadIdx = -1;
  // the form: bars into it (reset by a victory), the lead resting until a mood bar
  private formBar = 0;
  private leadRestUntil = 0;
  private readonly motifs = new Map<string, Motif>();
  private pending: Omit<NoteEvent, "layer">[] = [];
  private anchor: number | null = null;
  /** Units the lead has played since it entered (or came back after a rest): its first is always a statement. */
  private leadUnits = 0;
  private padPrev: number[] | null = null;
  private arpPrev: number[] | null = null;
  private padChordPrev: number[] | null = null;
  private arpChordPrev: number[] | null = null;
  /** Everything that happened, oldest first. */
  readonly log: MusicLogEntry[] = [];
  /** Notes scheduled, by layer. */
  readonly counts: Record<MusicLayer, number> = { base: 0, combat: 0, sting: 0 };
  /** When each bar was scheduled to start (the last 64), for tests of the grid. */
  readonly bars: number[] = [];
  /** The chords as they sounded (the last 256), for the chord-tone check. */
  readonly chords: ChordMark[] = [];

  constructor(
    private readonly table: MusicTable,
    private readonly sink: MusicSink,
    o: { seed?: number; mood?: string } = {},
  ) {
    if (!table.moods.length) throw new Error("music: no moods");
    for (const m of table.moods) this.moods.set(m.id, m);
    this.cur = this.moods.get(o.mood ?? table.moods[0]!.id) ?? table.moods[0]!;
    this.wanted = this.cur.id;
    this.rng = MUSIC_FAULTS.unseeded ? Math.random : mulberry32(o.seed ?? 1);
  }

  /** Seconds per bar in the current mood. */
  barLength(): number {
    return (60 / this.cur.bpm) * this.cur.beatsPerBar;
  }
  /** The first bar line at or after `t` on the current grid. */
  nextBarLine(t: number): number {
    const L = this.barLength();
    return this.t0 + Math.ceil((t - this.t0 - 1e-9) / L) * L;
  }
  /** Ask for a mood; it starts on a bar line. */
  mood(id: string, now: number): void {
    if (!this.moods.has(id)) throw new Error(`music: no mood "${id}"`);
    if (id !== this.wanted) {
      this.wanted = id;
      this.wantedAt = now;
    }
  }
  /** An enemy turned alert (on) or the last one stopped being alert (off). */
  alert(on: boolean, now: number): void {
    if (on && !this.alertOn) this.alertOnAt = now;
    if (!on && this.alertOn) this.alertOffAt = now;
    this.alertOn = on;
  }
  /** The fight was won: the victory phrase on the next bar line. */
  victory(now: number): void {
    this.victoryAsked = now;
    this.alertOn = false;
    this.alertOffAt = -Infinity;
  }
  /** Start the grid at `now` (the first bar line). */
  start(now: number): void {
    this.t0 = now;
    this.barIndex = 0;
    this.formBar = 0;
    this.nextBar = now;
    this.sink.tempo?.(this.cur.bpm, now);
  }
  /** Schedule every bar that starts before `now + lookahead`. */
  update(now: number, lookahead = 0.5): void {
    if (this.nextBar === null) this.start(now);
    // a stalled clock (a hidden tab) must not schedule a burst of past bars: skip to the grid's next line
    if (this.nextBar! < now - 0.05) {
      const L = this.barLength();
      const skip = Math.ceil((now - this.nextBar!) / L);
      this.barIndex += skip;
      this.formBar += skip;
      this.nextBar = this.nextBar! + skip * L;
      this.pending = [];
    }
    while (this.nextBar! < now + lookahead) this.scheduleBar(this.nextBar!, now);
  }
  /** The state for the handle. */
  state(): { mood: string; wanted: string; combat: boolean; alert: boolean; barLength: number; nextBar: number | null; counts: Record<MusicLayer, number>; section: number; bar: number } {
    const sec = this.section();
    return { mood: this.cur.id, wanted: this.wanted, combat: this.combatOn, alert: this.alertOn, barLength: this.barLength(), nextBar: this.nextBar, counts: { ...this.counts }, section: sec.index, bar: sec.bar };
  }

  private emit(e: NoteEvent): void {
    this.counts[e.layer]++;
    this.sink.note(e);
  }

  /** A base-layer note, humanised (timing and velocity) unless the planted `flat` defect is on. */
  private base(e: Omit<NoteEvent, "layer">): void {
    const h = this.cur.humanise;
    if (h && !MUSIC_FAULTS.flat) {
      const dt = (this.rng() * 2 - 1) * h.time;
      const dv = 1 + (this.rng() * 2 - 1) * h.vel;
      this.emit({ ...e, at: e.at + dt, grid: e.at, vel: Math.min(1, e.vel * dv), layer: "base" });
    } else this.emit({ ...e, grid: e.at, layer: "base" });
  }

  private from(part: { from?: number } | undefined): boolean {
    return !!part && (MUSIC_FAULTS.allAtOnce || this.barIndex >= (part.from ?? 0));
  }

  /** The section the form is in: its index (looped), the bar in it, its chords and its length in bars. */
  private section(): { index: number; bar: number; def: FormSection | null; chords: number[][]; bars: number; count: number } {
    const m = this.cur;
    const bpc = Math.max(1, m.barsPerChord ?? 1);
    if (!m.form?.length) {
      const bars = m.progression.length * bpc;
      return { index: Math.floor(this.formBar / bars), bar: this.formBar % bars, def: null, chords: m.progression, bars, count: Math.floor(this.formBar / bars) };
    }
    const lens = m.form.map((s) => this.chordsOf(s.chords).length * bpc);
    const total = lens.reduce((a, b) => a + b, 0);
    let b = this.formBar % total;
    const cycle = Math.floor(this.formBar / total);
    for (let i = 0; i < m.form.length; i++) {
      if (b < lens[i]!) return { index: i, bar: b, def: m.form[i]!, chords: this.chordsOf(m.form[i]!.chords), bars: lens[i]!, count: cycle * m.form.length + i };
      b -= lens[i]!;
    }
    return { index: 0, bar: 0, def: m.form[0]!, chords: m.progression, bars: lens[0]!, count: 0 };
  }
  private chordsOf(name: string): number[][] {
    return this.cur.progressions?.[name] ?? this.cur.progression;
  }

  private scheduleBar(B: number, now: number): void {
    // a mood change: the new grid starts on this bar line
    if (this.wanted !== this.cur.id) {
      this.cur = this.moods.get(this.wanted)!;
      this.t0 = B;
      this.barIndex = 0;
      this.formBar = 0;
      this.leadRestUntil = 0;
      this.pending = [];
      this.anchor = null;
      this.leadUnits = 0;
      this.phraseLeft = 0;
      this.restLeft = 1;
      this.log.push({ kind: "mood", at: B, asked: this.wantedAt, by: now, mood: this.cur.id });
      this.sink.tempo?.(this.cur.bpm, B);
      this.sink.cut?.(B);
    }
    const m = this.cur;
    const spb = 60 / m.bpm;
    const L = spb * m.beatsPerBar;
    this.bars.push(B);
    if (this.bars.length > 64) this.bars.shift();
    let stingBars = 0;

    // the victory phrase lands here, and the form starts again from its tonic under it
    if (this.victoryAsked !== null) {
      this.log.push({ kind: "victory", at: B, asked: this.victoryAsked, by: now, mood: m.id });
      this.victoryAsked = null;
      if (this.combatOn) {
        this.combatOn = false;
        this.sink.fade("combat", 0, B, 0.25);
      }
      const v = this.table.victory;
      v.notes.forEach((n, i) => this.emit({ at: B + i * v.step * spb, inst: v.inst, midi: m.root + n, vel: v.vel, dur: v.step * spb * (i === v.notes.length - 1 ? 4 : 1.2), layer: "sting", part: "victory" }));
      stingBars = Math.ceil((v.notes.length * v.step) / m.beatsPerBar);
      // the lead rests under the phrase and a bar after it
      this.phraseLeft = 0;
      this.restLeft = Math.max(this.restLeft, stingBars + 1);
      if (m.form?.length) {
        this.formBar = 0;
        this.pending = [];
        this.leadRestUntil = this.barIndex + stingBars + 1;
        this.leadUnits = 0;
        this.sink.cut?.(B);
      }
    }

    const sec = this.section();
    const bpc = Math.max(1, m.barsPerChord ?? 1);
    const ci = Math.floor(sec.bar / bpc);
    const chord = sec.chords[ci % sec.chords.length] ?? [0, 4, 7];
    const newChord = sec.bar % bpc === 0;
    const lastBarOfChord = sec.bar % bpc === bpc - 1;
    if (newChord || this.chords.length === 0 || this.chords.at(-1)!.mood !== m.id) {
      this.chords.push({ at: B, mood: m.id, chord: [...chord], root: m.root });
      if (this.chords.length > 256) this.chords.shift();
    }
    const rests = new Set(sec.def?.rest ?? []);
    const swell = this.swellAt(sec.bar, sec.bars);

    // the combat layer: in on a bar line (the fade over the beat before it, so the downbeat is at full volume), out
    // after its hold
    if (this.alertOn && !this.combatOn) {
      this.combatOn = true;
      this.sink.fade("combat", 1, Math.max(now, B - spb), Math.min(spb, Math.max(0.01, B - now)));
      this.log.push({ kind: "combat-in", at: B, asked: this.alertOnAt, by: now, mood: m.id });
    } else if (!this.alertOn && this.combatOn && B >= this.alertOffAt + this.table.combat.hold) {
      this.combatOn = false;
      this.sink.fade("combat", 0, B, this.table.combat.fadeOut);
      this.log.push({ kind: "combat-out", at: B, asked: this.alertOffAt, by: now, mood: m.id });
    }

    // the base: pad, bass, arpeggio, lead
    if (this.from(m.pad) && !rests.has("pad")) this.padBar(m, B, L, chord, newChord, bpc, swell);
    if (this.from(m.bass) && !rests.has("bass")) this.bassBar(m, B, spb, chord, lastBarOfChord ? (sec.chords[(ci + 1) % sec.chords.length] ?? chord) : null, swell);
    if (this.from(m.arp) && !rests.has("arp")) this.arpBar(m, B, spb, chord, newChord, swell);
    if (m.lead && this.from(m.lead)) {
      if (m.form?.length) this.formLeadBar(m, B, spb, sec, chord, swell);
      else this.leadBar(m, B, spb, chord);
    }

    // the combat pattern on this bar's 16 steps
    if (this.combatOn) {
      const step = L / 16;
      for (const p of this.table.combat.parts) for (const s of p.steps) this.emit({ at: B + s * step, inst: p.inst, midi: p.fixed ? p.note : m.root + chord[0]! + p.note, vel: p.vel, dur: step * 2, layer: "combat", part: "combat" });
    }

    this.barIndex++;
    this.formBar++;
    this.nextBar = B + L;
  }

  /** The swell: quiet at a section's ends, loud in its middle. */
  private swellAt(bar: number, bars: number): number {
    const s = MUSIC_FAULTS.flat ? 0 : (this.cur.swell ?? 0);
    if (!s) return 1;
    return 1 - s + 2 * s * Math.sin((Math.PI * (bar + 0.5)) / bars);
  }

  private padBar(m: MusicMood, B: number, L: number, chord: number[], newChord: boolean, bpc: number, swell: number): void {
    const p = m.pad!;
    if (!p.range) {
      // the first version: a block chord every bar
      for (const c of chord) this.base({ at: B, inst: p.inst, midi: m.root + 12 * p.octave + c, vel: p.vel, dur: L, part: "pad" });
      return;
    }
    if (!newChord) return;
    const use = MUSIC_FAULTS.staleChord && this.padChordPrev ? this.padChordPrev : chord;
    this.padChordPrev = chord;
    const o = { root: m.root, low: p.range[0], high: p.range[1], voices: p.voices ?? 4 };
    const v = MUSIC_FAULTS.noVoiceLeading ? blockVoicing(use, o) : voiceLead(use, this.padPrev, o);
    this.padPrev = v;
    for (const n of v) this.base({ at: B, inst: p.inst, midi: n, vel: p.vel * swell, dur: L * bpc, part: "pad" });
  }

  private bassBar(m: MusicMood, B: number, spb: number, chord: number[], next: number[] | null, swell: number): void {
    const b = m.bass!;
    const rootNote = m.root + 12 * b.octave + chord[0]!;
    b.beats.forEach((beat, k) => {
      const end = b.beats[k + 1] ?? m.beatsPerBar;
      const legacy = !b.notes && !b.walk;
      let midi = rootNote + (b.notes?.[k] ?? 0);
      if (b.walk && next && k === b.beats.length - 1 && k > 0) {
        // walk to the scale step next to the coming root, from the side we're on
        const target = m.root + 12 * b.octave + next[0]!;
        const pool = scalePool(m.root, m.scale, target - 3, target + 3).filter((n) => n !== target);
        const below = pool.filter((n) => n < target).at(-1);
        const above = pool.find((n) => n > target);
        const from = rootNote + (b.notes?.[k - 1] ?? 0);
        midi = (from <= target ? below : above) ?? below ?? above ?? midi;
      }
      this.base({ at: B + beat * spb, inst: b.inst, midi, vel: b.vel * (legacy ? 1 : swell), dur: legacy ? spb * 1.5 : (end - beat) * spb * 0.92, part: "bass" });
    });
  }

  private arpBar(m: MusicMood, B: number, spb: number, chord: number[], newChord: boolean, swell: number): void {
    const a = m.arp!;
    if (newChord || !this.arpPrev) {
      const use = MUSIC_FAULTS.staleChord && this.arpChordPrev ? this.arpChordPrev : chord;
      this.arpChordPrev = chord;
      const o = { root: m.root, low: a.range[0], high: a.range[1], voices: a.voices, omitRoot: false };
      this.arpPrev = MUSIC_FAULTS.noVoiceLeading ? blockVoicing(use, o) : voiceLead(use, this.arpPrev, o);
    }
    const v = this.arpPrev;
    const steps = Math.round(m.beatsPerBar / a.step);
    for (let s = 0; s < steps; s++) {
      const idx = a.pattern[s % a.pattern.length]!;
      if (idx < 0) continue;
      const midi = v[idx % v.length]! + 12 * Math.floor(idx / v.length);
      const beat = s * a.step;
      const accent = beat === 0 ? 1.12 : beat % 1 === 0 ? 1 : 0.84;
      this.base({ at: B + beat * spb, inst: a.inst, midi, vel: a.vel * accent * swell, dur: a.step * spb * (a.ring ?? 2), part: "arp" });
    }
  }

  // ---- the lead with a form: motifs, units, cadences

  /** A motif: a rhythm from the lead's cells and a contour of scale steps (an arch for A; a leap then a fall for B). */
  private makeMotif(m: MusicMood, material: "A" | "B"): Motif {
    const lead = m.lead!;
    const bars = lead.motifBars ?? 2;
    const cells = lead.cells.length ? lead.cells : [[m.beatsPerBar]];
    const notes: MotifNote[] = [];
    let beat = 0;
    for (let b = 0; b < bars; b++) {
      let pool = cells;
      // B: the cells with the fewest notes (longer values); the motif's last bar ends long (a breath)
      if (material === "B") {
        const min = Math.min(...cells.map((c) => c.length));
        pool = cells.filter((c) => c.length <= min + 1);
      }
      if (b === bars - 1) {
        const long = pool.filter((c) => c[c.length - 1]! >= 1.5);
        if (long.length) pool = long;
      }
      for (const d of pick(this.rng, pool)) {
        notes.push({ beat, dur: d, step: 0 });
        beat += d;
      }
    }
    let s = 0;
    const n = notes.length;
    notes.forEach((x, k) => {
      if (k === 0) return;
      const u = k / Math.max(1, n - 1);
      let d = pick(this.rng, [-2, -1, -1, 1, 1, 2, 0, -3, 3]);
      if (material === "B" && k === 1) d = 3 + Math.floor(this.rng() * 2);
      else if (material === "B" && u > 0.3 && this.rng() < 0.55) d = -Math.abs(d) || -1;
      else {
        if (u < 0.5 && this.rng() < 0.4) d = Math.abs(d) || 1;
        if (u > 0.6 && this.rng() < 0.4) d = -Math.abs(d) || -1;
      }
      // no repeated pitch twice in a row, and stay within a sixth or so of the start (bouncing back off the edges)
      if (d === 0 && k > 1) d = this.rng() < 0.5 ? 1 : -1;
      if (s + d < -4 || s + d > 5) d = -d;
      s = Math.max(-4, Math.min(5, s + d));
      x.step = s;
    });
    return { notes, beats: beat };
  }

  private motif(m: MusicMood, material: "A" | "B"): Motif {
    const key = `${m.id}:${material}`;
    let mo = this.motifs.get(key);
    if (!mo || MUSIC_FAULTS.noMotif) {
      mo = this.makeMotif(m, material);
      if (!MUSIC_FAULTS.noMotif) this.motifs.set(key, mo);
    }
    return mo;
  }

  private formLeadBar(m: MusicMood, B: number, spb: number, sec: ReturnType<MusicDirector["section"]>, chord: number[], swell: number): void {
    const lead = m.lead!;
    const def = sec.def!;
    const bars = lead.motifBars ?? 2;
    // the unit starting on this bar: compose it (its notes go out bar by bar, so a mood change or a victory drops the rest)
    if (sec.bar % bars === 0 && def.lead && this.barIndex >= this.leadRestUntil) {
      this.pending = this.composeUnit(m, B, spb, sec, chord, Math.floor(sec.bar / bars), swell);
    } else if (sec.bar % bars === 0) this.pending = [];
    const L = spb * m.beatsPerBar;
    const now = this.pending.filter((e) => e.at < B + L - 1e-9);
    this.pending = this.pending.filter((e) => e.at >= B + L - 1e-9);
    for (const e of now) this.base(e);
  }

  private composeUnit(m: MusicMood, B: number, spb: number, sec: ReturnType<MusicDirector["section"]>, chord: number[], unit: number, swell: number): Omit<NoteEvent, "layer">[] {
    const lead = m.lead!;
    const def = sec.def!;
    const bars = lead.motifBars ?? 2;
    const units = Math.max(1, Math.floor(sec.bars / bars));
    const material = def.lead === "B" ? "B" : "A";
    // the lead's first unit (entering, or back after a rest) states the motif wherever it falls; then by position
    const role: PhraseMark["role"] = this.leadUnits === 0 || unit === 0 ? "statement" : unit === units - 1 ? "answer" : unit === 1 && units > 2 ? "repeat" : "variation";
    this.leadUnits++;
    const pool = scalePool(m.root, m.scale, lead.low, lead.high);
    if (!pool.length) return [];
    const centre = nearestIndex(pool, (lead.low + lead.high) / 2) + (material === "B" ? 2 : 0);
    const mo = this.motif(m, material);
    // the anchor: a chord tone near the last anchor (the statement: near the middle of the range), with room for the
    // motif's whole contour inside the range (so a repeat keeps its shape exactly)
    const lo = -Math.min(0, ...mo.notes.map((x) => x.step));
    const hi = pool.length - 1 - Math.max(0, ...mo.notes.map((x) => x.step));
    const from = role === "statement" || this.anchor === null ? centre : this.anchor;
    const clampA = (i: number): number => Math.max(Math.min(lo, pool.length - 1), Math.min(Math.max(hi, 0), i));
    let anchor = snapToChord(pool, clampA(from), m.root, chord);
    if (anchor < lo || anchor > hi) {
      // the nearest chord tone fell outside the room: the nearest one inside it, else the clamped index
      const inside = pool.map((_, j) => j).filter((j) => j >= lo && j <= hi && chord.some((c) => pcOf(c) === pcOf(pool[j]!, m.root)));
      anchor = inside.length ? inside.reduce((a, b) => (Math.abs(b - from) < Math.abs(a - from) ? b : a)) : clampA(from);
    }
    this.anchor = anchor;
    let notes: MotifNote[] = mo.notes.map((x) => ({ ...x }));
    let snapLong = false;
    if (role === "variation") {
      notes = this.vary(notes);
      snapLong = true;
    } else if (role === "answer") notes = this.answer(m, notes, bars);
    const inst = def.inst ?? lead.inst;
    const out: Omit<NoteEvent, "layer">[] = [];
    const chordAt = (beat: number): number[] => {
      const bpc = Math.max(1, m.barsPerChord ?? 1);
      const bar = sec.bar + Math.floor(beat / m.beatsPerBar);
      return sec.chords[Math.floor(bar / bpc) % sec.chords.length] ?? chord;
    };
    notes.forEach((x, k) => {
      let i = Math.max(0, Math.min(pool.length - 1, anchor + x.step));
      const last = k === notes.length - 1;
      if (role === "answer" && last) {
        // the cadence: home on the root (A2), on the fifth (B), on the third or fifth (A)
        const c = chordAt(x.beat);
        const targets = def.lead === "A2" ? [0] : def.lead === "B" ? [7] : [pcOf(c[1] ?? 4), pcOf(c[2] ?? 7)];
        const cands = pool.map((n, j) => ({ n, j })).filter((p) => targets.includes(pcOf(p.n, m.root)));
        if (cands.length) i = cands.reduce((a, b) => (Math.abs(b.j - i) < Math.abs(a.j - i) ? b : a)).j;
      } else if ((snapLong && (x.dur >= 1.5 || x.beat % m.beatsPerBar === 0)) || (role === "answer" && x.beat === 0)) i = snapToChord(pool, i, m.root, chordAt(x.beat));
      const accent = x.beat % m.beatsPerBar === 0 ? 1.08 : 1;
      out.push({
        at: B + x.beat * spb,
        inst,
        midi: pool[i]!,
        vel: lead.vel * swell * accent * (last ? 0.88 : 1),
        dur: x.dur * spb * (last ? 1.5 : 1.05),
        part: "lead",
        phrase: { section: sec.count, unit, role, material: def.lead === "A2" ? "A2" : material, beat: x.beat, step: i - anchor },
      });
    });
    return out;
  }

  /** The motif with its rhythm tweaked: a long note split in two (the second a step away), or two short notes merged. */
  private vary(notes: MotifNote[]): MotifNote[] {
    const out = notes.map((x) => ({ ...x }));
    const longs = out.map((x, k) => ({ x, k })).filter(({ x, k }) => x.dur >= 1 && k < out.length - 1);
    if (longs.length && this.rng() < 0.65) {
      const { x, k } = pick(this.rng, longs);
      const half = x.dur / 2;
      x.dur = half;
      out.splice(k + 1, 0, { beat: x.beat + half, dur: half, step: x.step + (this.rng() < 0.5 ? 1 : -1) });
    } else {
      const pairs = out.map((x, k) => ({ x, k })).filter(({ x, k }) => k < out.length - 2 && x.dur + out[k + 1]!.dur <= 2);
      if (pairs.length) {
        const { x, k } = pick(this.rng, pairs);
        x.dur += out[k + 1]!.dur;
        out.splice(k + 1, 1);
      }
    }
    return out;
  }

  /** The answer: the motif's opening (all but its last bar; half of a one-bar motif) with its contour turned over,
   *  then a long note to the end (the cadence). */
  private answer(m: MusicMood, notes: MotifNote[], bars: number): MotifNote[] {
    const split = bars === 1 ? m.beatsPerBar / 2 : (bars - 1) * m.beatsPerBar;
    const head = notes.filter((x) => x.beat < split).map((x) => ({ ...x, dur: Math.min(x.dur, split - x.beat), step: -x.step }));
    return [...head, { beat: split, dur: bars * m.beatsPerBar - split, step: 0 }];
  }

  // ---- the lead without a form: free phrases (the first version)

  private leadBar(m: MusicMood, B: number, spb: number, chord: number[]): void {
    const lead = m.lead!;
    if (this.phraseLeft <= 0) {
      if (this.restLeft > 0) {
        this.restLeft--;
        return;
      }
      const pb = lead.phraseBars ?? [2, 3];
      const rb = lead.restBars ?? [1, 2];
      this.phraseLeft = pb[0] + Math.floor(this.rng() * (pb[1] - pb[0] + 1));
      this.restLeft = rb[0] + Math.floor(this.rng() * (rb[1] - rb[0] + 1));
      this.leadIdx = -1;
    }
    const pool = scalePool(m.root, m.scale, lead.low, lead.high);
    if (!pool.length) return;
    const snap = (i: number): number => snapToChord(pool, i, m.root, chord);
    if (this.leadIdx < 0) this.leadIdx = snap(Math.floor(pool.length / 2 - this.rng() * 3));
    const cell = lead.cells[Math.floor(this.rng() * lead.cells.length)] ?? [m.beatsPerBar];
    const last = this.phraseLeft === 1;
    let beat = 0;
    cell.forEach((dur, k) => {
      const final = last && k === cell.length - 1;
      if (k > 0 || beat > 0) {
        // stepwise, with an arch: rising early in the phrase, falling at its end
        const steps = [-2, -1, -1, 1, 1, 2, 0];
        let s = steps[Math.floor(this.rng() * steps.length)]!;
        if (!last && this.rng() < 0.35) s = Math.abs(s) || 1;
        if (last && this.rng() < 0.45) s = -Math.abs(s) || -1;
        this.leadIdx = Math.min(pool.length - 1, Math.max(0, this.leadIdx + s));
      }
      if (dur >= 1.5 || k === 0 || final) this.leadIdx = snap(this.leadIdx);
      this.base({ at: B + beat * spb, inst: lead.inst, midi: pool[this.leadIdx]!, vel: lead.vel * (final ? 0.85 : 1), dur: dur * spb * (final ? 1.6 : 1), part: "lead" });
      beat += dur;
    });
    this.phraseLeft--;
  }
}
